import React, { useEffect, useState } from "react";
import "./CommunityQuestionsModal.scss";
import { AlertService, http } from "../fx-core";

interface QuestionOption {
  id: number;
  questionId: number;
  optionText: string;
  optUniqueId?: number;
  isAdd?: boolean;
}

interface Question {
  id: number;
  description: string;
  questionTypeId: number;
  questionType: string;
  targetType: number;
  queUniqueId?: number;
  questionOptions?: QuestionOption[];
}

interface Props {
  visible: boolean;
  communityId: number;
  targetType: number;
  onClose: () => void;
}

export default function CommunityQuestionsModal({
  visible,
  communityId,
  targetType,
  onClose,
}: Props) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [loading, setLoading] = useState(false);
  const [hasQuestions, setHasQuestions] = useState(false);
  const [showResidentWarning, setShowResidentWarning] = useState(false);
  const [baseCommunity, setBaseCommunity] = useState({ id: 0, name: "" });

  useEffect(() => {
    if (visible && communityId) {
      getCommunityQuestionsWithOptions();
      getUserBaseCommunity();
    } else {
      setQuestions([]);
      setAnswers({});
      setHasQuestions(false);
      setShowResidentWarning(false);
    }
  }, [visible, communityId]);

  const getCommunityQuestionsWithOptions = () => {
    const apiOptions: any = {
      url: `question/community/${communityId}`,
    };
    http.get(apiOptions).then((res) => {
      getCommunityQuestionOptionsCallBack(res);
    }).catch((error) => {
      console.error("Error fetching community questions:", error);
      onClose();
    });
  };

  const getUserBaseCommunity = () => {
    const apiOptions: any = {
      url: `community/user/base`,
      data: {}
    };
    http.post(apiOptions).then(res => {
      const result = res.data;
      setBaseCommunity(result);
    }).catch(error => {
      console.error("Error fetching base community:", error);
    });
  };

  const getCommunityQuestionOptionsCallBack = (res: any) => {
    const result = res.data;
    const fetchedQuestions: Question[] = (result.questions || []).filter(
      (q: Question) => Number(q.targetType) === Number(targetType)
    );
    const fetchedOptions: QuestionOption[] = result.questionOptions || [];

    console.log("All questions:", result.questions);
    console.log("Filtered questions for targetType", targetType, ":", fetchedQuestions);
    console.log("All options:", fetchedOptions);

    if (fetchedQuestions.length === 0) {
      console.log("No questions found for targetType:", targetType);
      // Mark as answered even if no questions to prevent showing popup again
      markCommunityAsAnswered();
      onClose();
      return;
    }

    setHasQuestions(true);

    for (let q of fetchedQuestions) {
      q.queUniqueId = q.id;

      if (q.questionTypeId === 3) {
        const optionsForQ = fetchedOptions.filter((i) => i.questionId === q.id);
        if (optionsForQ.length > 0) {
          for (let o of optionsForQ) {
            o.optUniqueId = o.id;
            o.isAdd = false;
          }
          q.questionOptions = optionsForQ;
        } else {
          q.questionOptions = [];
        }
      } else {
        q.questionOptions = [];
      }
    }

    setQuestions(fetchedQuestions);
  };

  const markCommunityAsAnswered = () => {
    const answeredCommunities = JSON.parse(sessionStorage.getItem("answeredCommunities") || "[]");
    if (!answeredCommunities.includes(communityId)) {
      answeredCommunities.push(communityId);
      sessionStorage.setItem("answeredCommunities", JSON.stringify(answeredCommunities));
    }
  };

  const handleAnswerChange = (questionId: number, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = () => {
    const unanswered = questions.filter(
      (q) => !answers[q.id] || answers[q.id].trim() === ""
    );

    if (unanswered.length > 0) {
      AlertService.showErrorMsg("Please answer all required questions");
      return;
    }

    // Check if user answered "no" to ANY question (case insensitive)
    const hasNoAnswer = Object.values(answers).some(answer => 
      answer.toLowerCase().trim() === 'no'
    );

    if (hasNoAnswer) {
      // Show resident warning popup - DON'T mark as answered here
      setShowResidentWarning(true);
    } else {
      // Only mark as answered when user selects "yes" to all questions
      submitAnswers();
    }
  };

  const submitAnswers = () => {
    setLoading(true);
    setTimeout(() => {
      console.log("Submitting answers:", answers);
      
      // Mark community as answered after submission (only for "yes" answers)
      markCommunityAsAnswered();
      
      setLoading(false);
      onClose();
    }, 800);
  };

  const handleSwitchToBaseCommunity = () => {
    // DON'T mark as answered - so they see the question again when they come back
    setShowResidentWarning(false);
    onClose();
    
    // Switch to base community
    switchToBaseCommunity();
  };

  const switchToBaseCommunity = () => {
    const apiOptions: any = {
      url: `community/user/base`,
      data: {}
    };
    
    http.post(apiOptions).then(res => {
      const baseCommunity = res.data;
      if (baseCommunity && baseCommunity.id > 0) {
        // Switch to base community
        switchCommunity(baseCommunity.id);
      }
    }).catch(error => {
      console.error("Error switching to base community:", error);
    });
  };

  const switchCommunity = (targetCommunityId: number) => {
    const inputData = {
      communityId: targetCommunityId
    };
    
    const apiOptions: any = {
      url: `community/switch`,
      data: inputData
    };
    
    http.post(apiOptions).then(res => {
      const result = res.data;
      if (result && result.linkUrl) {
        // Navigate to base community
        window.open(result.linkUrl, '_self');
      }
    }).catch(error => {
      console.error("Error switching community:", error);
    });
  };

  if (!visible) return null;

  return (
    <>
      {/* Main Questions Modal */}
      <div className="modal-overlay">
        <div className="modal-popup">
          <h2>Community Questions</h2>

          <div className="questions-container">
            {questions.map((q) => (
              <div className="question-block" key={q.id}>
                <label>
                  {q.description} <span style={{ color: "red" }}>*</span>
                </label>

                {q.questionTypeId === 3 && q.questionOptions?.length ? (
                  <select
                    required
                    value={answers[q.id] || ""}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                  >
                    <option value="">Select</option>
                    {q.questionOptions.map((opt) => (
                      <option key={opt.optUniqueId} value={opt.optionText}>
                        {opt.optionText}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    required
                    value={answers[q.id] || ""}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>

          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>

      {/* Resident Warning Modal */}
      {showResidentWarning && (
        <div className="modal-overlay">
          <div className="modal-popup warning-popup">
            <h3>Community Access</h3>
            <p>
              This community is only available for its part of the community.
            </p>
            
            <div className="warning-actions">
              <button 
                style={{background:"#f69516",color:"white"}}
                onClick={handleSwitchToBaseCommunity}
              >
               Back to {baseCommunity.name ? ` ${baseCommunity.name}` : "Your area"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}